import { useTranslation } from 'react-i18next'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  PED_CLASSIFICATION_VALUES,
  PED_EQUIPMENT_KIND_VALUES,
  type PedApplicabilityFields,
} from '../utils/pedApplicabilityQuestionnaire'

type V2PedApplicabilityConfirmationFieldsProps = {
  fields: PedApplicabilityFields
  disabled: boolean
  showClassification: boolean
  onChange: (fields: PedApplicabilityFields) => void
}

export function V2PedApplicabilityConfirmationFields({
  fields,
  disabled,
  showClassification,
  onChange,
}: V2PedApplicabilityConfirmationFieldsProps) {
  const { t } = useTranslation('common')

  return (
    <div className="space-y-4 rounded-md border p-3">
      <p className="text-sm font-medium">
        {t('v2.applicability.ped.confirmation.title')}
      </p>
      <div className="space-y-2">
        <Label htmlFor="ped-equipment-kind">
          {t('v2.applicability.ped.confirmation.equipmentKindLabel')}
        </Label>
        <Select
          value={fields.equipmentKind ?? ''}
          onValueChange={(value) => {
            const nextKind = value as PedApplicabilityFields['equipmentKind']
            onChange({
              equipmentKind: nextKind,
              classification: null,
            })
          }}
          disabled={disabled}
        >
          <SelectTrigger id="ped-equipment-kind">
            <SelectValue
              placeholder={t(
                'v2.applicability.ped.confirmation.equipmentKindPlaceholder'
              )}
            />
          </SelectTrigger>
          <SelectContent>
            {PED_EQUIPMENT_KIND_VALUES.map((kind) => (
              <SelectItem key={kind} value={kind}>
                {t(`v2.applicability.ped.confirmation.equipmentKind.${kind}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {showClassification && (
        <div className="space-y-2">
          <Label htmlFor="ped-classification">
            {t('v2.applicability.ped.confirmation.classificationLabel')}
          </Label>
          <p className="text-xs text-muted-foreground">
            {t('v2.applicability.ped.confirmation.classificationHelper')}
          </p>
          <Select
            value={fields.classification ?? ''}
            onValueChange={(value) => {
              onChange({
                ...fields,
                classification: value as PedApplicabilityFields['classification'],
              })
            }}
            disabled={disabled || fields.equipmentKind == null}
          >
            <SelectTrigger id="ped-classification">
              <SelectValue
                placeholder={t(
                  'v2.applicability.ped.confirmation.classificationPlaceholder'
                )}
              />
            </SelectTrigger>
            <SelectContent>
              {PED_CLASSIFICATION_VALUES.map((classification) => (
                <SelectItem key={classification} value={classification}>
                  {t(
                    `v2.applicability.ped.confirmation.classification.${classification}`
                  )}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  )
}
